import GoogleMapReact, { Coords } from 'google-map-react';
import config from 'config/config';
import { Box, forwardRef } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  AddressSelector,
  AddressGeoCodeSelector,
  MapDetailSelector,
  PanelSelector,
  RoofAngleSelector,
} from 'store/selectors';
import PanelLine from 'features/map/PanelLine';
import {
  calculatePanelGeoPoint,
  // getNumPanels,
  metersPerPx,
} from 'features/map/mapUtils';

import { RootState } from 'store/store';
import { updateMapDetails } from 'store/answers/answersSlice';

import { GetPanelSizeOnMapWithRoofAngle } from 'utils/calculation/calculation';
import usePanelList from 'pages/PagePanel/hooks/usePanelList';
import { GeoPoint } from 'schemas/geoPointSchema';
import { AnswersState } from 'schemas/answerStateSchema';
import { PanelLineState } from 'schemas/panelLineStateSchema';
import { getPanelOptions } from 'utils/getPanelOptions';

const DRAWING_ZOOM = 21;

/**
 * wrapper component for the element that must follow the map
 */
const DrawingArea = forwardRef(
  (
    {
      mapScale,
      children,
    }: {
      lat: number;
      lng: number;
      mapScale: number;
      children: React.ReactNode;
    },
    ref
  ) => (
    <Box
      ref={ref}
      pointerEvents="none"
      transform={`translate(-50%, -50%) scale(${mapScale})`}
      // background="rgb(0, 151, 19, 0.7)" // this will never be applied has this is a wrapper with 0width and 0height
      // width="500px"
      // height="500px"
      role="button"
      tabIndex={0}
      onKeyDown={() => {}}
      // {...props}
    >
      {children}
    </Box>
  )
);

/**
 * This helper return the ratio needed between the "drawing" map level and the current map level
 * To allow the panels to be a the correct size
 */
const getMapScaleValue = (mapZoom: number, lat: number) => {
  const drawingValue = metersPerPx(DRAWING_ZOOM, lat);
  const zoomValue = metersPerPx(mapZoom, lat);
  return drawingValue / zoomValue;
};

type MapEditionProps = {
  height: string | number;
  previewOnly?: boolean;
};

const Map = ({ height, previewOnly = false }: MapEditionProps) => {
  const dispatch = useDispatch();

  const userAdress = useSelector(AddressSelector);
  const addressGeoloc = useSelector(AddressGeoCodeSelector);
  const [calculatedMapCenter, setCalculatedMapCenter] = useState<
    GeoPoint | undefined
  >(undefined);

  const mapDetails = useSelector<RootState, AnswersState['mapDetail']>(
    MapDetailSelector
  );
  const panelList = usePanelList();
  const panelId = useSelector(PanelSelector);

  // get panels and calculate size depending on roof angle
  const panelOptions = getPanelOptions(panelId, panelList);
  const roofAngle = useSelector(RoofAngleSelector);
  const panelSize = panelOptions ? { ...panelOptions.size } : undefined;
  if (panelOptions && panelSize && roofAngle) {
    panelSize.h = GetPanelSizeOnMapWithRoofAngle(
      roofAngle,
      panelOptions.size.h
    );
  }

  const [zoom, setZoom] = useState<number>(DRAWING_ZOOM);
  const [zooming, setZooming] = useState<boolean>(false);
  const [allowDrag, setAllowDrag] = useState<boolean>(true);
  const canvasHeight = height || {
    base: 'calc(100vh - 360px)',
    md: 'calc(100vh - 300px)',
  };

  const [panelLines, setPanelLines] = useState<PanelLineState[] | undefined>(
    mapDetails?.panels
  );
  // const [selectedPanel, setSelectedPanel] = useState<string | undefined>();
  const mapScale = addressGeoloc
    ? getMapScaleValue(zoom, addressGeoloc.lat)
    : 1;

  const totalPanels = panelLines?.reduce(
    (acc, current) => acc + current.panelAmount,
    0
  );

  // --------------------- methods ------------------------

  const updateStore = (newPanelLines: PanelLineState[]) => {
    if (addressGeoloc && newPanelLines) {
      dispatch(
        updateMapDetails({
          lat: (addressGeoloc as GeoPoint).lat,
          lng: (addressGeoloc as GeoPoint).lng,
          panels: newPanelLines,
        })
      );
    }
  };

  const onGoogleMapChange = (change: GoogleMapReact.ChangeEventValue) => {
    setZoom(change.zoom);
  };

  const handlePanelLineChange = (pState: PanelLineState) => {
    if (!panelLines || !pState.angle) {
      throw new Error(
        `we should never update store with no angle or no panels for panel line:${JSON.stringify(
          pState
        )}`
      );
      return;
    }

    const newPanelLines = panelLines.map((panel) =>
      panel.id === pState.id ? pState : panel
    );
    setPanelLines(newPanelLines);
    // setSelectedPanel(pState.id);
    updateStore(newPanelLines);
  };

  // --------------------- effects ------------------------

  // calculated map center
  useEffect(() => {
    if (!calculatedMapCenter && panelLines && panelLines.length > 0) {
      setCalculatedMapCenter(
        calculatePanelGeoPoint(addressGeoloc, zoom, panelLines[0])
      );
    }
  }, [
    calculatedMapCenter,
    setCalculatedMapCenter,
    panelLines,
    zoom,
    addressGeoloc,
  ]);

  // --------------------- render ------------------------

  if (!userAdress) {
    // TODO: what we do in this case?
    return <div>NO USER ADDRESS</div>;
  }

  return (
    <Box
      width="100%"
      height={canvasHeight} // Important! Always set the container height explicitly for google map to work
    >
      {addressGeoloc && (
        <GoogleMapReact
          bootstrapURLKeys={{
            key: config.GOOGLE_API_KEY,
            libraries: ['visualization', 'places', 'drawing'],
            id: '__googleMapsScriptId',
          }}
          center={(calculatedMapCenter || addressGeoloc) as Coords}
          defaultZoom={previewOnly && totalPanels > 50 ? 19 : DRAWING_ZOOM}
          onChange={onGoogleMapChange}
          onZoomAnimationStart={() => setZooming(true)}
          onZoomAnimationEnd={() => setZooming(false)}
          options={(_maps) => ({
            // DOC OPTIONS: https://developers.google.com/maps/documentation/javascript/reference/map#MapOptions.clickableIcons
            mapTypeId: 'satellite',
            disableDefaultUI: true,
            zoomControl: false,
            keyboardShortcuts: false,
            zoomControlOptions: { position: 8 },
            fullscreenControl: false,
            gestureHandling: allowDrag ? 'auto' : 'none',
            minZoom: 19,
            rotateControl: false,
            tilt: 0,
          })}
        >
          {addressGeoloc && panelSize && (
            <DrawingArea
              lat={addressGeoloc.lat}
              lng={addressGeoloc.lng}
              mapScale={mapScale}
            >
              <Box
                visibility={!zooming ? 'visible' : 'hidden'}
                onMouseDown={() => setAllowDrag(false)}
                onTouchStart={() => setAllowDrag(false)}
                onMouseUp={() => setAllowDrag(true)}
                onTouchEnd={() => setAllowDrag(true)}
              >
                {panelLines?.map((panel) => (
                  <PanelLine
                    key={panel.id}
                    scaleRatio={mapScale}
                    initialState={panel}
                    metersPerPixel={metersPerPx(
                      DRAWING_ZOOM,
                      addressGeoloc.lat
                    )}
                    panelSizeInMeter={panelSize}
                    onChangeEnd={handlePanelLineChange}
                    previewOnly={previewOnly}
                  />
                ))}
              </Box>
            </DrawingArea>
          )}
        </GoogleMapReact>
      )}
    </Box>
  );
};

export default Map;
